import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Fragment,
} from 'react';
import { Col } from 'reactstrap';
import { ApplicationFormTemplate } from '../../../../../../../../entities/v1/applicant_tracking/ApplicationFormTemplate';
import { ApplicationFormTemplateService } from '../../../../../../../../services/v1/applicant_tracking/ApplicationFormTemplateService';
import { ApplicationListingFilters } from '../../utils';
import { Option } from '../../../../../../../../entities/v1/survey_engine/Option';
import { SelectOption } from '../../../../../../../../components/Select';
import { QuestionSelector } from './components/QuestionSelector';
import { AnswerInput } from './components/AnswerInput';
import {
  getAllQuestionsFromTemplates,
  mapQuestionsToSelectOptions,
  buildQuestionJobNames,
  resolveAppliedJobNames,
  findQuestionByTitle,
  QuestionSelectOption,
} from './utils';

interface PropTypes {
  isLoading: boolean;
  filters: ApplicationListingFilters;
  setFilters: (filters: ApplicationListingFilters) => void;
  appliedJobs?: SelectOption[];
}

// Combined state for question-related information
interface QuestionState {
  selectedTitle: string | null;
  type: string | null;
  options: Option[];
  isLoading: boolean;
}

// Initial/empty state for resetting
const initialQuestionState: QuestionState = {
  selectedTitle: null,
  type: null,
  options: [],
  isLoading: false,
};

export function ApplicationFormFilter(props: PropTypes) {
  const [templates, setTemplates] = useState<ApplicationFormTemplate[]>([]);
  const [questionState, setQuestionState] = useState<QuestionState>({
    ...initialQuestionState,
    isLoading: true,
  });
  const [preloadedOptions, setPreloadedOptions] = useState<
    QuestionSelectOption[]
  >([]);

  const optionsLoadCallbacks = useRef<
    { resolve: (options: QuestionSelectOption[]) => void }[]
  >([]);

  const loadedForJobIdsRef = useRef<string | null>(null);

  const questionTitle = props.filters?.applicationFormQuestionTitle || null;
  const answerValue = props.filters?.applicationFormAnswerValue || null;

  const appliedJobIds = useMemo(
    () =>
      (props.appliedJobs || [])
        .map((job) => parseInt(job.value, 10))
        .filter((id) => !isNaN(id)),
    [props.appliedJobs],
  );

  const appliedJobIdsKey = appliedJobIds.join(',');

  // Job name tooltips are only shown when 2 or more job filters are applied
  const showJobNameTooltips = appliedJobIds.length >= 2;

  const [questionJobNames, setQuestionJobNames] = useState<
    Map<string, string[]> | undefined
  >(undefined);

  // Helper function to reset question state
  const resetQuestionState = () => {
    setQuestionState(initialQuestionState);
  };

  // Helper to update question state from a question
  const updateQuestionStateFromQuestion = (
    question: QuestionSelectOption | null,
  ) => {
    if (!question) {
      resetQuestionState();
      return null;
    }

    const newState = {
      selectedTitle: question.value,
      type: question.data?.fieldType || null,
      options: question.data?.options || [],
      isLoading: false,
    };

    setQuestionState(newState);
    return newState;
  };

  // Fetch application form templates, scoped to the applied job filters
  const fetchApplicationFormTemplates = useCallback(async () => {
    try {
      setQuestionState((prev) => ({ ...prev, isLoading: true }));

      const [response, appliedJobsWithNames] = await Promise.all([
        ApplicationFormTemplateService.list({
          jobIds: appliedJobIds.length > 0 ? appliedJobIds : undefined,
          includeFields: showJobNameTooltips ? ['job_ids'] : undefined,
          expand: [
            'survey_template',
            'survey_template.sections',
            'survey_template.sections.questions',
            'survey_template.sections.questions.options',
          ],
        }),
        showJobNameTooltips
          ? resolveAppliedJobNames(props.appliedJobs || [])
          : Promise.resolve([]),
      ]);

      if (response?.applicationFormTemplates) {
        const validTemplates = response.applicationFormTemplates.filter(
          (template: ApplicationFormTemplate) =>
            template?.id && template?.surveyTemplate?.name,
        );

        setTemplates(validTemplates);

        const jobNames = showJobNameTooltips
          ? buildQuestionJobNames(validTemplates, appliedJobsWithNames)
          : undefined;
        setQuestionJobNames(jobNames);

        // Create options from templates
        const allQuestions = getAllQuestionsFromTemplates(validTemplates);
        const validQuestions = allQuestions.filter(
          (q) => q?.fieldType !== null && q?.title,
        );
        const options = mapQuestionsToSelectOptions(
          validQuestions,
          undefined,
          jobNames,
        );

        setPreloadedOptions(options);

        // Resolve any pending requests for options
        optionsLoadCallbacks.current.forEach(({ resolve }) =>
          resolve([...options]),
        );
        optionsLoadCallbacks.current = [];
      }
    } catch (error) {
      setTemplates([]);
      setPreloadedOptions([]);
      setQuestionJobNames(undefined);

      // Resolve any pending requests with empty array
      optionsLoadCallbacks.current.forEach(({ resolve }) => resolve([]));
      optionsLoadCallbacks.current = [];
    } finally {
      loadedForJobIdsRef.current = appliedJobIdsKey;
      setQuestionState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [props.appliedJobs]);

  // Load templates on mount and whenever a search applies new job filters
  useEffect(() => {
    fetchApplicationFormTemplates();
  }, [fetchApplicationFormTemplates]);

  // Sync component state with external filters
  useEffect(() => {
    if (!questionTitle || templates.length === 0) {
      resetQuestionState();
      return;
    }

    const matchingQuestion = findQuestionByTitle(templates, questionTitle);

    if (matchingQuestion) {
      setQuestionState({
        selectedTitle: matchingQuestion.title,
        type: matchingQuestion.fieldType,
        options: matchingQuestion.options || [],
        isLoading: false,
      });
    } else {
      resetQuestionState();
    }
  }, [questionTitle, templates]);

  // Handle question selection change
  const handleQuestionChange = (option: QuestionSelectOption | null) => {
    if (!option) {
      // Clear filters if no question selected
      props.setFilters({
        ...props.filters,
        applicationFormQuestionTitle: null,
        applicationFormAnswerValue: null,
      });
      resetQuestionState();
      return;
    }

    // Update question state
    const newState = updateQuestionStateFromQuestion(option);
    if (!newState) return;

    // Determine default answer value based on question type
    let defaultAnswerValue: string | null = null;

    if (newState.type === 'boolean') {
      // Default to 'true' for boolean questions
      defaultAnswerValue = 'true';
    } else if (
      newState.type === 'options' ||
      newState.type === 'multi_options'
    ) {
      // Use first option's name for select questions if options exist
      if (newState.options.length > 0) {
        defaultAnswerValue = newState.options[0].name || '';
      }
    }

    // Update filters
    props.setFilters({
      ...props.filters,
      applicationFormQuestionTitle: newState.selectedTitle,
      applicationFormAnswerValue: defaultAnswerValue,
    });
  };

  // Handle answer input change
  const handleAnswerChange = (value: string) => {
    props.setFilters({
      ...props.filters,
      applicationFormAnswerValue: value.trim() === '' ? null : value,
    });
  };

  // Load question options for async select
  const loadQuestionOptions = async (
    inputValue: string,
  ): Promise<QuestionSelectOption[]> => {
    // Only serve templates fetched for the currently applied job filters
    if (loadedForJobIdsRef.current === appliedJobIdsKey) {
      if (templates.length > 0) {
        return getFilteredQuestionOptions(inputValue);
      }

      return preloadedOptions;
    }

    // Register a callback to be called when templates are loaded
    return new Promise<QuestionSelectOption[]>((resolve) => {
      optionsLoadCallbacks.current.push({ resolve });

      // Safety timeout - resolve empty after 10 seconds
      setTimeout(() => {
        const index = optionsLoadCallbacks.current.findIndex(
          (callback) => callback.resolve === resolve,
        );
        if (index !== -1) {
          optionsLoadCallbacks.current.splice(index, 1);
          resolve([]);
        }
      }, 10000);
    });
  };

  // Helper function to filter questions by search input
  const getFilteredQuestionOptions = (
    inputValue: string,
  ): QuestionSelectOption[] => {
    const allQuestions = getAllQuestionsFromTemplates(templates);
    const validQuestions = allQuestions.filter(
      (q) => q?.fieldType !== null && q?.title,
    );

    // Filter questions by search input
    const filteredQuestions = inputValue
      ? validQuestions.filter((q) =>
          q.title.toLowerCase().includes(inputValue.toLowerCase()),
        )
      : validQuestions;

    return mapQuestionsToSelectOptions(
      filteredQuestions,
      questionState.selectedTitle || undefined,
      questionJobNames,
    );
  };

  // Get selected question option for dropdown display
  const getSelectedQuestionOption = () => {
    if (!questionState.selectedTitle) return null;

    const allQuestions = getAllQuestionsFromTemplates(templates);
    const matchingQuestion = allQuestions.find(
      (q) => q.title === questionState.selectedTitle,
    );

    if (!matchingQuestion) return null;

    return {
      value: matchingQuestion.title,
      label: matchingQuestion.title,
      data: {
        fieldType: matchingQuestion.fieldType,
        options: matchingQuestion.options || [],
      },
    } as QuestionSelectOption;
  };

  // Combine loading states for simplicity
  const isLoading = props.isLoading || questionState.isLoading;

  // Return a Fragment with two Columns instead of one wrapping Column
  return (
    <Fragment>
      <Col xs='3' className='mt-3h'>
        <label>
          <h5 className=''>Custom Question</h5>
        </label>
        <QuestionSelector
          key={appliedJobIdsKey}
          isLoading={isLoading}
          loadOptions={loadQuestionOptions}
          onChange={handleQuestionChange}
          getSelectedQuestionOption={getSelectedQuestionOption}
        />
      </Col>
      <Col xs='3' className='mt-3h'>
        <label>
          <h5 className='text-white'>Answer</h5>{' '}
          {/* Hidden Label for alignment */}
        </label>
        <AnswerInput
          selectedQuestionType={questionState.type}
          questionTitle={questionTitle}
          answerValue={answerValue}
          questionOptions={questionState.options}
          isLoading={isLoading}
          onAnswerChange={handleAnswerChange}
        />
      </Col>
    </Fragment>
  );
}
